import React, { Component } from "react";
import PropTypes from "prop-types";

import { isDefined, isNotDefined, noop } from "../utils";

import { terminate, saveNodeType, isHoverForInteractiveType } from "./utils";

import EachFibonacciTimeZones from "./wrapper/EachFibonacciTimeZones";
import MouseLocationIndicator from "./components/MouseLocationIndicator";
import HoverTextNearMouse from "./components/HoverTextNearMouse";

class FibonacciTimeZones extends Component {
  constructor(props) {
    super(props);

    this.handleStart = this.handleStart.bind(this);
    this.handleEnd = this.handleEnd.bind(this);
    this.handleDrawLine = this.handleDrawLine.bind(this);
    this.handleDragLine = this.handleDragLine.bind(this);
    this.handleDragLineComplete = this.handleDragLineComplete.bind(this);

    this.terminate = terminate.bind(this);
    this.saveNodeType = saveNodeType.bind(this);

    this.getSelectionState = isHoverForInteractiveType("timeZones").bind(this);

    this.state = {
      current: null,
      override: null,
    };
    this.nodes = [];
  }

  handleDragLine(index, newXYValue) {
    this.setState({
      override: {
        index,
        ...newXYValue,
      },
    });
  }

  handleDragLineComplete(moreProps) {
    const { override } = this.state;
    if (isDefined(override)) {
      const { timeZones } = this.props;
      const newTimeZones = timeZones.map((each, idx) =>
        idx === override.index
          ? {
              ...each,
              x1: override.x1,
              x2: override.x2,
              selected: true,
            }
          : {
              ...each,
              selected: false,
            },
      );

      this.setState(
        {
          override: null,
        },
        () => {
          this.props.onComplete(newTimeZones, moreProps);
        },
      );
    }
  }

  handleDrawLine(xyValue) {
    const { current } = this.state;
    if (isDefined(current) && isDefined(current.x1)) {
      this.mouseMoved = true;
      this.setState({
        current: {
          x1: current.x1,
          x2: xyValue[0], // Only update time (x), not price (y)
        },
      });
    }
  }

  handleStart(xyValue, moreProps, e) {
    const { current } = this.state;

    if (isNotDefined(current) || isNotDefined(current.x1)) {
      this.mouseMoved = false;

      this.setState(
        {
          current: {
            x1: xyValue[0], // Start time
            x2: null,
          },
        },
        () => {
          this.props.onStart(moreProps, e);
        },
      );
    }
  }

  handleEnd(xyValue, moreProps, e) {
    const { current } = this.state;
    const { timeZones, appearance } = this.props;

    if (this.mouseMoved && isDefined(current) && isDefined(current.x1)) {
      const currentAppearance = appearance;

      const newTimeZones = [
        ...timeZones.map((d) => ({ ...d, selected: false })),
        {
          x1: current.x1,
          x2: xyValue[0], // End time
          selected: true,
          appearance: currentAppearance,
        },
      ];

      this.setState(
        {
          current: null,
        },
        () => {
          this.props.onComplete(newTimeZones, moreProps, e);
        },
      );
    }
  }

  render() {
    const { appearance } = this.props;
    const { enabled, chartSectionId } = this.props;
    const { currentPositionRadius, currentPositionStroke } = this.props;
    const { currentPositionStrokeOpacity, currentPositionStrokeWidth } =
      this.props;
    const { hoverText, timeZones } = this.props;
    const { current, override } = this.state;

    const hoverTextWithDefaults = {
      ...FibonacciTimeZones.defaultProps.hoverText,
      ...hoverText,
    };

    return (
      <g>
        {timeZones.map((each, idx) => {
          // Properly merge appearance objects
          let eachAppearance = appearance;
          if (isDefined(each.appearance)) {
            eachAppearance = { ...appearance };
            // Deep merge for stroke object
            if (
              each.appearance.stroke &&
              typeof each.appearance.stroke === "object"
            ) {
              eachAppearance.stroke = {
                ...(appearance.stroke || {}),
                ...(each.appearance.stroke || {}),
              };
            } else if (each.appearance.stroke) {
              eachAppearance.stroke = each.appearance.stroke;
            }
            // Deep merge for font object
            if (
              each.appearance.font &&
              typeof each.appearance.font === "object"
            ) {
              eachAppearance.font = {
                ...(appearance.font || {}),
                ...(each.appearance.font || {}),
              };
            } else if (each.appearance.font) {
              eachAppearance.font = each.appearance.font;
            }
            // Deep merge for fill object
            if (
              each.appearance.fill &&
              typeof each.appearance.fill === "object"
            ) {
              eachAppearance.fill = {
                ...(appearance.fill || {}),
                ...(each.appearance.fill || {}),
              };
            } else if (each.appearance.fill) {
              eachAppearance.fill = each.appearance.fill;
            }
            // Handle other properties
            Object.keys(each.appearance).forEach((key) => {
              if (key !== "stroke" && key !== "font" && key !== "fill") {
                eachAppearance[key] = each.appearance[key];
              }
            });
          }

          return (
            <EachFibonacciTimeZones
              key={idx}
              ref={this.saveNodeType(idx)}
              index={idx}
              {...(idx === override?.index ? override : each)}
              appearance={eachAppearance}
              hoverText={hoverTextWithDefaults}
              onDrag={this.handleDragLine}
              onDragComplete={this.handleDragLineComplete}
              chartSectionId={chartSectionId}
            />
          );
        })}

        {/* Preview line during drawing */}
        {isDefined(current) && isDefined(current.x2) && (
          <EachFibonacciTimeZones
            interactive={false}
            x1={current.x1}
            x2={current.x2}
            appearance={appearance}
            hoverText={hoverTextWithDefaults}
          />
        )}

        <MouseLocationIndicator
          enabled={enabled}
          snap={false}
          r={currentPositionRadius}
          stroke={currentPositionStroke}
          opacity={currentPositionStrokeOpacity}
          strokeWidth={currentPositionStrokeWidth}
          onMouseDown={this.handleStart}
          onClick={this.handleEnd}
          onMouseMove={this.handleDrawLine}
          data-evo-id="interactive-fibonacci-time-zones"
        />
      </g>
    );
  }
}

FibonacciTimeZones.propTypes = {
  enabled: PropTypes.bool.isRequired,
  chartSectionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  onStart: PropTypes.func,
  onComplete: PropTypes.func,
  onSelect: PropTypes.func,

  hoverText: PropTypes.object,

  currentPositionStroke: PropTypes.string,
  currentPositionStrokeWidth: PropTypes.number,
  currentPositionStrokeOpacity: PropTypes.number,
  currentPositionRadius: PropTypes.number,

  appearance: PropTypes.shape({
    stroke: PropTypes.oneOfType([
      PropTypes.shape({
        color: PropTypes.string,
        dash: PropTypes.string,
        width: PropTypes.number,
        opacity: PropTypes.number,
      }),
      PropTypes.string,
    ]),
    font: PropTypes.shape({
      fontFamily: PropTypes.string,
      fontSize: PropTypes.number,
      color: PropTypes.string,
    }),
    fill: PropTypes.shape({
      color: PropTypes.string,
      opacity: PropTypes.number,
    }),
  }).isRequired,

  timeZones: PropTypes.array.isRequired,
};

FibonacciTimeZones.defaultProps = {
  timeZones: [],

  onStart: noop,
  onComplete: noop,
  onSelect: noop,

  currentPositionStroke: "#FF9800",
  currentPositionStrokeOpacity: 1,
  currentPositionStrokeWidth: 2,
  currentPositionRadius: 4,

  hoverText: {
    ...HoverTextNearMouse.defaultProps,
    enable: false,
    bgHeight: "auto",
    bgWidth: "auto",
    text: "Clique e arraste para criar Zonas Tempo Fibonacci",
    selectedText: "",
  },
};

export default FibonacciTimeZones;
